import React, { useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { getUsers } from '../../actions/userActions';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const UserRegistrationChart = ({ getUsers, users }) => {
  useEffect(() => {
    getUsers();
  }, [getUsers]);

  // Ensure users is always an array
  const usersArray = Array.isArray(users) ? users : [];

  // Group users by registration date
  const registrationsByDate = usersArray.reduce((acc, user) => {
    const date = new Date(user.createdAt).toLocaleDateString();
    acc[date] = (acc[date] || 0) + 1;
    return acc;
  }, {});

  // Sort dates chronologically
  const sortedDates = Object.keys(registrationsByDate).sort((a, b) => 
    new Date(a) - new Date(b)
  );

  const data = {
    labels: sortedDates,
    datasets: [
      {
        label: 'New Registrations',
        data: sortedDates.map(date => registrationsByDate[date]),
        borderColor: '#3b82f6', // blue-500
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: '#3b82f6',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const options = {
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.raw;
            return `${value} new registration${value !== 1 ? 's' : ''}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        },
        title: {
          display: true,
          text: 'Number of Registrations'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Date'
        }
      }
    },
    maintainAspectRatio: false,
  };

  return (
    <div className="bg-white rounded-xl shadow p-6 w-full max-w-4xl mx-auto">
      <div className="flex flex-col items-start mb-6">
        <h2 className="text-xl font-bold text-gray-900">User Registrations Over Time</h2>
        <span className="text-gray-500 text-sm">Daily registration statistics</span>
      </div>
      <div className="h-80">
        <Line data={data} options={options} />
      </div>
    </div>
  );
};

UserRegistrationChart.propTypes = {
  getUsers: PropTypes.func.isRequired,
  users: PropTypes.array
};

const mapStateToProps = state => ({
  users: state.user.users
});

export default connect(mapStateToProps, { getUsers })(UserRegistrationChart); 
